import React from 'react';
import './ChatErrorBoundary.css';

/**
 * Error Boundary for Chat Components
 * Provides graceful degradation when chat features fail
 */
class ChatErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false,
            error: null,
            errorInfo: null,
            resetKey: props.resetKey,
        };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    // FIX #11: Auto-reset error state when resetKey changes (e.g., conversation switch)
    static getDerivedStateFromProps(props, state) {
        if (props.resetKey !== undefined && props.resetKey !== state.resetKey) {
            return {
                hasError: false,
                error: null,
                errorInfo: null,
                resetKey: props.resetKey,
            };
        }
        return null;
    }

    componentDidCatch(error, errorInfo) {
        // Log to console for debugging
        console.error('[Chat Error Boundary] Caught error:', error, errorInfo);

        // Store error info for display
        this.setState({ errorInfo });

        // TODO: Log to error monitoring service (Sentry, LogRocket, etc.)
        // if (window.Sentry) {
        //   window.Sentry.captureException(error, { extra: errorInfo });
        // }
    }

    handleRetry = () => {
        this.setState({
            hasError: false,
            error: null,
            errorInfo: null,
        });
    };

    render() {
        if (this.state.hasError) {
            return (
                <div className="chat-error-boundary">
                    <div className="chat-error-boundary__content">
                        <svg
                            className="chat-error-boundary__icon"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            width="48"
                            height="48"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                        <h3 className="chat-error-boundary__title">Chat temporarily unavailable</h3>
                        <p className="chat-error-boundary__message">
                            We're having trouble loading the chat. This could be due to a network issue or temporary service disruption.
                        </p>
                        <button
                            className="chat-error-boundary__retry-btn"
                            onClick={this.handleRetry}
                        >
                            Retry
                        </button>
                        {process.env.NODE_ENV === 'development' && this.state.error && (
                            <details className="chat-error-boundary__details">
                                <summary>Error details (dev only)</summary>
                                <pre className="chat-error-boundary__stack">
                                    {this.state.error.toString()}
                                    {this.state.errorInfo?.componentStack}
                                </pre>
                            </details>
                        )}
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ChatErrorBoundary;
